// AI Learning Assistant Data
// Structured for Supabase migration with multi-language support

export const aiLearningAssistantData = {
  // AI Learning Assistant Overview
  overview: {
    id: "ai_learning_assistant",
    title: {
      tc: "AI 學習助手",
      sc: "AI 学习助手", 
      en: "AI Learning Assistant"
    },
    subtitle: {
      tc: "智能化的個人學習夥伴",
      sc: "智能化的个人学习伙伴",
      en: "Intelligent Personal Learning Partner"
    },
    icon: "brain",
    color: "blue"
  },

  // AI Features
  features: [
    {
      id: "smart_learning_plan",
      title: {
        tc: "智能學習計劃",
        sc: "智能学习计划",
        en: "Smart Learning Plan"
      },
      description: {
        tc: "AI 根據你的進度和時間安排最適合的學習計劃",
        sc: "AI 根据你的进度和时间安排最适合的学习计划",
        en: "AI arranges the most suitable learning plan based on your progress and time"
      },
      icon: "calendar",
      buttonText: {
        tc: "查看今日計劃",
        sc: "查看今日计划",
        en: "View Today's Plan"
      },
      buttonColor: "blue",
      action: "view_today_plan"
    },
    {
      id: "learning_reminder",
      title: {
        tc: "學習提醒",
        sc: "学习提醒",
        en: "Learning Reminder"
      },
      description: {
        tc: "智能提醒最佳學習時間,幫助你建立學習習慣",
        sc: "智能提醒最佳学习时间,帮助你建立学习习惯",
        en: "Smartly reminds you of the best learning time, helping you build learning habits"
      },
      icon: "clock",
      buttonText: {
        tc: "設定提醒",
        sc: "设定提醒",
        en: "Set Reminder"
      },
      buttonColor: "green",
      action: "set_reminder"
    },
    {
      id: "progress_analysis",
      title: {
        tc: "進度分析",
        sc: "进度分析",
        en: "Progress Analysis"
      },
      description: {
        tc: "詳細分析你的學習模式和改進建議",
        sc: "详细分析你的学习模式和改进建议",
        en: "Detailed analysis of your learning patterns and improvement suggestions"
      },
      icon: "trending-up",
      buttonText: {
        tc: "查看分析",
        sc: "查看分析",
        en: "View Analysis"
      },
      buttonColor: "purple",
      action: "view_analysis"
    },
    {
      id: "instant_qa",
      title: {
        tc: "即時問答",
        sc: "即时问答",
        en: "Instant Q&A"
      },
      description: {
        tc: "隨時詢問英語問題,獲得即時專業解答",
        sc: "随时询问英语问题,获得即时专业解答",
        en: "Ask English questions anytime, get instant professional answers"
      },
      icon: "zap",
      buttonText: {
        tc: "開始提問",
        sc: "开始提问",
        en: "Start Asking"
      },
      buttonColor: "yellow",
      action: "start_asking"
    }
  ],

  // Weekly Learning Summary
  weeklySummary: {
    id: "weekly_learning_summary",
    title: {
      tc: "本週學習摘要",
      sc: "本周学习摘要",
      en: "This Week's Learning Summary"
    },
    stats: [
      {
        id: "study_hours",
        value: "4.5",
        label: {
          tc: "小時",
          sc: "小时",
          en: "Hours"
        },
        color: "blue"
      },
      {
        id: "average_score",
        value: "89%",
        label: {
          tc: "平均分數",
          sc: "平均分数",
          en: "Average Score"
        },
        color: "green"
      },
      {
        id: "completed_courses",
        value: "12",
        label: {
          tc: "完成課程",
          sc: "完成课程",
          en: "Completed Courses"
        },
        color: "purple"
      },
      {
        id: "new_vocabulary",
        value: "+15",
        label: {
          tc: "新詞彙",
          sc: "新词汇",
          en: "New Vocabulary"
        },
        color: "orange"
      }
    ]
  },

  // Today's Learning Plan
  todayPlan: {
    id: "today_learning_plan",
    title: {
      tc: "今日學習計劃",
      sc: "今日学习计划",
      en: "Today's Learning Plan"
    },
    tasks: [
      {
        id: "morning_vocabulary",
        time: "09:00",
        title: {
          tc: "晨間詞彙練習",
          sc: "晨间词汇练习",
          en: "Morning Vocabulary Practice"
        },
        duration: "15分鐘",
        status: "completed"
      },
      {
        id: "grammar_lesson",
        time: "14:00",
        title: {
          tc: "語法課程學習",
          sc: "语法课程学习",
          en: "Grammar Lesson"
        },
        duration: "30分鐘",
        status: "in_progress"
      },
      {
        id: "speaking_practice",
        time: "18:00",
        title: {
          tc: "口語練習",
          sc: "口语练习",
          en: "Speaking Practice"
        },
        duration: "20分鐘",
        status: "pending"
      }
    ]
  },

  // Learning Reminders
  reminders: {
    id: "learning_reminders",
    title: {
      tc: "學習提醒設定",
      sc: "学习提醒设定",
      en: "Learning Reminder Settings"
    },
    options: [
      {
        id: "morning_reminder",
        time: "08:00",
        label: {
          tc: "晨間提醒",
          sc: "晨间提醒",
          en: "Morning Reminder"
        },
        enabled: true
      },
      {
        id: "afternoon_reminder",
        time: "14:00",
        label: {
          tc: "下午提醒",
          sc: "下午提醒",
          en: "Afternoon Reminder"
        },
        enabled: true
      },
      {
        id: "evening_reminder",
        time: "19:00",
        label: {
          tc: "晚間提醒",
          sc: "晚间提醒",
          en: "Evening Reminder"
        },
        enabled: false
      }
    ]
  },

  // Progress Analysis Data
  progressAnalysis: {
    id: "progress_analysis_data",
    title: {
      tc: "學習進度分析",
      sc: "学习进度分析",
      en: "Learning Progress Analysis"
    },
    charts: [
      {
        id: "weekly_progress",
        type: "line",
        title: {
          tc: "本週學習進度",
          sc: "本周学习进度",
          en: "This Week's Progress"
        },
        data: [
          { day: "週一", value: 85 },
          { day: "週二", value: 92 },
          { day: "週三", value: 78 },
          { day: "週四", value: 95 },
          { day: "週五", value: 88 },
          { day: "週六", value: 76 },
          { day: "週日", value: 82 }
        ]
      },
      {
        id: "skill_distribution",
        type: "doughnut",
        title: {
          tc: "技能分佈",
          sc: "技能分布",
          en: "Skill Distribution"
        },
        data: [
          { skill: "詞彙", value: 35 },
          { skill: "語法", value: 25 },
          { skill: "口語", value: 20 },
          { skill: "聽力", value: 20 }
        ]
      }
    ],
    insights: [
      {
        id: "strength_insight",
        type: "strength",
        title: {
          tc: "強項",
          sc: "强项",
          en: "Strengths"
        },
        content: {
          tc: "你的詞彙量增長很快，建議繼續保持",
          sc: "你的词汇量增长很快，建议继续保持",
          en: "Your vocabulary growth is fast, keep it up"
        }
      },
      {
        id: "improvement_insight",
        type: "improvement",
        title: {
          tc: "改進建議",
          sc: "改进建议",
          en: "Improvement Suggestions"
        },
        content: {
          tc: "口語練習時間可以增加，建議每天練習15分鐘",
          sc: "口语练习时间可以增加，建议每天练习15分钟",
          en: "Increase speaking practice time, recommend 15 minutes daily"
        }
      }
    ]
  },

  // Q&A System
  qaSystem: {
    id: "qa_system",
    title: {
      tc: "AI 即時問答",
      sc: "AI 即时问答",
      en: "AI Instant Q&A"
    },
    placeholder: {
      tc: "輸入你的英語問題...",
      sc: "输入你的英语问题...",
      en: "Enter your English question..."
    },
    recentQuestions: [
      {
        id: "q1",
        question: {
          tc: "什麼時候用 'a' 和 'an'？",
          sc: "什么时候用 'a' 和 'an'？",
          en: "When to use 'a' and 'an'?"
        },
        answer: {
          tc: "'a' 用於輔音音素開頭的詞，'an' 用於元音音素開頭的詞",
          sc: "'a' 用于辅音音素开头的词，'an' 用于元音音素开头的词",
          en: "'a' is used before words starting with consonant sounds, 'an' before vowel sounds"
        },
        timestamp: "2024-01-15T10:30:00Z"
      },
      {
        id: "q2",
        question: {
          tc: "過去完成時和現在完成時的區別？",
          sc: "过去完成时和现在完成时的区别？",
          en: "Difference between past perfect and present perfect?"
        },
        answer: {
          tc: "過去完成時表示過去某時間點之前完成的動作，現在完成時表示過去發生但與現在有關聯的動作",
          sc: "过去完成时表示过去某时间点之前完成的动作，现在完成时表示过去发生但与现在有关联的动作",
          en: "Past perfect indicates actions completed before a past time point, present perfect indicates past actions with present relevance"
        },
        timestamp: "2024-01-14T15:45:00Z"
      }
    ]
  }
};

// Database schema for Supabase migration
export const aiLearningAssistantSchema = {
  tables: [
    {
      name: "ai_learning_assistant_features",
      columns: [
        { name: "id", type: "uuid", primary: true },
        { name: "feature_id", type: "text", unique: true },
        { name: "title_tc", type: "text" },
        { name: "title_sc", type: "text" },
        { name: "title_en", type: "text" },
        { name: "description_tc", type: "text" },
        { name: "description_sc", type: "text" },
        { name: "description_en", type: "text" },
        { name: "icon", type: "text" },
        { name: "button_color", type: "text" },
        { name: "action", type: "text" },
        { name: "created_at", type: "timestamp", default: "now()" }
      ]
    },
    {
      name: "weekly_learning_summary",
      columns: [
        { name: "id", type: "uuid", primary: true },
        { name: "user_id", type: "uuid", foreign_key: "users.id" },
        { name: "week_start_date", type: "date" },
        { name: "study_hours", type: "decimal" },
        { name: "average_score", type: "decimal" },
        { name: "completed_courses", type: "integer" },
        { name: "new_vocabulary", type: "integer" },
        { name: "created_at", type: "timestamp", default: "now()" }
      ]
    },
    {
      name: "learning_reminders",
      columns: [
        { name: "id", type: "uuid", primary: true },
        { name: "user_id", type: "uuid", foreign_key: "users.id" },
        { name: "reminder_time", type: "time" },
        { name: "label_tc", type: "text" },
        { name: "label_sc", type: "text" },
        { name: "label_en", type: "text" },
        { name: "enabled", type: "boolean", default: true },
        { name: "created_at", type: "timestamp", default: "now()" }
      ]
    },
    {
      name: "ai_qa_history",
      columns: [
        { name: "id", type: "uuid", primary: true },
        { name: "user_id", type: "uuid", foreign_key: "users.id" },
        { name: "question_tc", type: "text" },
        { name: "question_sc", type: "text" },
        { name: "question_en", type: "text" },
        { name: "answer_tc", type: "text" },
        { name: "answer_sc", type: "text" },
        { name: "answer_en", type: "text" },
        { name: "created_at", type: "timestamp", default: "now()" }
      ]
    }
  ]
};
